import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navigation from '../Layouts/Navigation/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCommentDots,
    faCircleCheck,
    faCircleXmark,
    faPlus,
    faTrash,
    faToggleOn,
    faToggleOff,
    faPaperPlane,
    faSpinner,
    faShield,
    faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../utils/constants';

const BASE = `${BASE_URL}`;

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
};

export default function AdminWhatsapp() {
    const [status, setStatus] = useState(null);
    const [numeros, setNumeros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testLoading, setTestLoading] = useState(false);

    const [form, setForm] = useState({ nombre: '', numero: '', tipo: 'seguridad' });
    const [testNumero, setTestNumero] = useState('');
    const [testMsg, setTestMsg] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [statusRes, numerosRes] = await Promise.all([
                axios.get(`${BASE}/whatsapp/status`, getAuthHeaders()),
                axios.get(`${BASE}/whatsapp/numeros`, getAuthHeaders()),
            ]);
            setStatus(statusRes.data);
            setNumeros(numerosRes.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!form.nombre.trim() || !form.numero.trim()) return;
        setSaving(true);
        setTestMsg('');
        try {
            await axios.post(`${BASE}/whatsapp/numeros`, form, getAuthHeaders());
            setForm({ nombre: '', numero: '', tipo: 'seguridad' });
            await fetchData();
        } catch (err) {
            setTestMsg(err.response?.data?.error || 'Error al guardar el número.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (id) => {
        try {
            await axios.put(`${BASE}/whatsapp/numeros/${id}/toggle`, {}, getAuthHeaders());
            await fetchData();
        } catch (e) { console.error(e); }
    };

    const handleDelete = async (id, nombre) => {
        if (!window.confirm(`¿Eliminar el número de "${nombre}"?`)) return;
        try {
            await axios.delete(`${BASE}/whatsapp/numeros/${id}`, getAuthHeaders());
            await fetchData();
        } catch (e) { console.error(e); }
    };

    const handleTest = async (e) => {
        e.preventDefault();
        if (!testNumero.trim()) return;
        setTestLoading(true);
        setTestMsg('');
        try {
            const res = await axios.post(`${BASE}/whatsapp/test`, { numero: testNumero }, getAuthHeaders());
            setTestMsg(res.data.message || 'Mensaje enviado.');
        } catch (err) {
            setTestMsg(err.response?.data?.error || 'Error al enviar el mensaje.');
        } finally {
            setTestLoading(false);
        }
    };

    const tipoLabel = (tipo) =>
        tipo === 'admin'
            ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 font-semibold"><FontAwesomeIcon icon={faUserTie} />Admin</span>
            : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-semibold"><FontAwesomeIcon icon={faShield} />Seguridad</span>;

    return (
        <div className="min-h-screen bg-gray-100">
            <Navigation />
            <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">

                {/* Título */}
                <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon={faCommentDots} className="text-green-600 text-2xl" />
                    <h1 className="text-2xl font-bold text-gray-800">Configuración WhatsApp</h1>
                </div>

                {/* Estado de la integración */}
                {loading ? (
                    <div className="flex justify-center py-10">
                        <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-gray-400" />
                    </div>
                ) : (
                    <>
                        <div className={`flex items-start gap-4 p-4 rounded-xl border ${status?.configured ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                            <FontAwesomeIcon
                                icon={status?.configured ? faCircleCheck : faCircleXmark}
                                className={`text-2xl mt-0.5 ${status?.configured ? 'text-green-500' : 'text-yellow-500'}`}
                            />
                            <div>
                                <p className={`font-semibold ${status?.configured ? 'text-green-800' : 'text-yellow-800'}`}>
                                    {status?.configured ? 'WhatsApp conectado y activo' : 'WhatsApp pendiente de conexión'}
                                </p>
                                <p className="text-sm text-gray-600 mt-0.5">{status?.info}</p>
                            </div>
                        </div>

                        {!status?.configured && (
                            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
                                <h2 className="font-semibold text-gray-700">Cómo activar WhatsApp</h2>
                                <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-600">
                                    <li>Inicia el servidor backend (<code className="bg-gray-100 px-1 rounded">npm start</code>)</li>
                                    <li>En la consola del servidor aparece un <strong>código QR</strong></li>
                                    <li>Abre WhatsApp en tu teléfono y ve a <strong>Dispositivos vinculados</strong></li>
                                    <li>Toca <strong>Vincular un dispositivo</strong> y escanea el código QR</li>
                                    <li>Una vez escaneado, el servidor queda conectado y listo. La sesión se guarda automáticamente.</li>
                                </ol>
                                <p className="text-xs text-gray-500">No necesitas ninguna API ni cuenta de desarrollador. El número desde el que se envían los mensajes es el teléfono que vinculaste.</p>
                            </div>
                        )}

                        {/* Números registrados */}
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                                <h2 className="font-semibold text-gray-800">Números registrados</h2>
                                <span className="text-sm text-gray-500">{numeros.length} número(s)</span>
                            </div>

                            {numeros.length === 0 ? (
                                <div className="py-10 text-center text-gray-400 text-sm">
                                    No hay números configurados. Agrega el primero abajo.
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="text-left px-5 py-3 text-gray-500 font-medium">Nombre</th>
                                            <th className="text-left px-5 py-3 text-gray-500 font-medium">Número</th>
                                            <th className="text-left px-5 py-3 text-gray-500 font-medium">Tipo</th>
                                            <th className="text-center px-5 py-3 text-gray-500 font-medium">Activo</th>
                                            <th className="text-center px-5 py-3 text-gray-500 font-medium">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {numeros.map((n) => (
                                            <tr key={n.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-5 py-3 font-medium text-gray-800">{n.nombre}</td>
                                                <td className="px-5 py-3 text-gray-600 font-mono">{n.numero}</td>
                                                <td className="px-5 py-3">{tipoLabel(n.tipo)}</td>
                                                <td className="px-5 py-3 text-center">
                                                    <button
                                                        onClick={() => handleToggle(n.id)}
                                                        title={n.activo ? 'Desactivar' : 'Activar'}
                                                        className="text-xl transition-colors"
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={n.activo ? faToggleOn : faToggleOff}
                                                            className={n.activo ? 'text-green-500' : 'text-gray-400'}
                                                        />
                                                    </button>
                                                </td>
                                                <td className="px-5 py-3 text-center">
                                                    <button
                                                        onClick={() => handleDelete(n.id, n.nombre)}
                                                        title="Eliminar"
                                                        className="text-red-400 hover:text-red-600 transition-colors"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {/* Formulario agregar */}
                            <form onSubmit={handleAdd} className="border-t border-gray-100 px-5 py-4 bg-gray-50">
                                <p className="text-sm font-medium text-gray-700 mb-3">Agregar número</p>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <input
                                        type="text"
                                        placeholder="Nombre (ej: Juan Seguridad)"
                                        value={form.nombre}
                                        onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                                        required
                                    />
                                    <input
                                        type="text"
                                        placeholder="+57 300 000 0000"
                                        value={form.numero}
                                        onChange={(e) => setForm({ ...form, numero: e.target.value })}
                                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                                        required
                                    />
                                    <select
                                        value={form.tipo}
                                        onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                                    >
                                        <option value="seguridad">Seguridad</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60"
                                    >
                                        {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
                                        Agregar
                                    </button>
                                </div>
                                {testMsg && <p className="mt-2 text-sm text-red-600">{testMsg}</p>}
                            </form>
                        </div>

                        {/* Prueba de envío */}
                        <div className="bg-white border border-gray-200 rounded-xl p-5">
                            <h2 className="font-semibold text-gray-800 mb-3">Prueba de envío</h2>
                            <p className="text-sm text-gray-500 mb-4">
                                Envía un mensaje de prueba a cualquier número para verificar que la integración funciona correctamente.
                            </p>
                            <form onSubmit={handleTest} className="flex flex-col sm:flex-row gap-3">
                                <input
                                    type="text"
                                    placeholder="+57 300 000 0000"
                                    value={testNumero}
                                    onChange={(e) => setTestNumero(e.target.value)}
                                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    required
                                />
                                <button
                                    type="submit"
                                    disabled={testLoading || !status?.configured}
                                    title={!status?.configured ? 'WhatsApp no está configurado' : ''}
                                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60"
                                >
                                    {testLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPaperPlane} />}
                                    Enviar prueba
                                </button>
                            </form>
                            {testMsg && (
                                <p className={`mt-3 text-sm ${testMsg.toLowerCase().includes('error') ? 'text-red-600' : 'text-green-600'}`}>
                                    {testMsg}
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
